import {Button} from "../../../basic/button";
import {Badge} from "../../../basic/badge";
import {Label} from "../../../basic/label";
import {Copy} from "lucide-react";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {VwpLicense} from "../../../../store/generated/api";
import {LicenseExpirationLabel} from "../../../shared/LicenseExpirationLabel";
import {LicenseStatusChip} from "../../../shared/status/LicenseStatusChip";
import {toast} from "sonner";

interface TeamLicensesProps {
    teamLicenses: VwpLicense[];
}

export function TeamLicenses({teamLicenses}: TeamLicensesProps) {
    const {t} = useTranslationWithKeys();

    const handleCopyLicenseId = (licenseId: string) => {
        if (licenseId) {
            navigator.clipboard.writeText(licenseId);
            toast.success(t('admin.teams.teamManagement.licenseIdCopied'));
        }
    };

    return (
        <div className="space-y-4 pt-4 border-t">
            <Label>{t('admin.teams.teamManagement.teamLicenses')}</Label>
            <div className="space-y-2">
                {teamLicenses.map((license) => (
                    <div
                        key={license.id}
                        className="p-3 rounded-lg border bg-muted/30 flex justify-between items-center cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                        <div>
                            <p className="text-sm font-medium">{license.product}</p>
                            <div className="flex items-center gap-2">
                                <p className="text-xs text-muted-foreground">{t('admin.licenses.editLicense.licenseId')}: {license.id}</p>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleCopyLicenseId(license.id)}
                                    className="h-5 w-5"
                                    title={t('common.copy')}
                                >
                                    <Copy className="h-3.5 w-3.5"/>
                                </Button>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                                {license.trialStatus === 'IN_TRIAL' && (
                                    <Badge variant="warning" className="text-xs">
                                        {t('licenses.trialStatus.inTrial')}
                                    </Badge>
                                )}
                                {license.supportLevel === 'SMA' && (
                                    <Badge variant="secondary" className="text-xs">
                                        {t('licenses.sma')}
                                    </Badge>
                                )}
                                {license.supportLevel === 'SMA_PLUS' && (
                                    <Badge variant="secondary" className="text-xs">
                                        {t('licenses.smaPlus')}
                                    </Badge>
                                )}
                            </div>
                            <p className="text-muted-foreground text-sm mt-1">
                                {t('admin.teams.teamManagement.licenseInfo', {
                                    assigned: license.assigned,
                                    quantity: license.quantity,
                                    expires: ''
                                })}
                                {' '}
                                <LicenseExpirationLabel license={license} tooltipMode="detailed"/>
                            </p>
                        </div>
                        <LicenseStatusChip license={license}/>
                    </div>
                ))}
                {teamLicenses.length === 0 && (
                    <p className="text-muted-foreground p-3">{t('admin.teams.teamManagement.noLicenses')}</p>
                )}
            </div>
        </div>
    );
}